<template>
  <div
    ref="bottomPanelRoot"
    v-show="visible"
    class="map-kit-bottom-panel"
    :class="{ 'is-fullscreen': fullscreen }"
    :style="{
      '--mk-bottom-panel-content-header-height': axisAreaHeight + 'px',
      ...(!fullscreen ? { height: panelHeight + 'px' } : {}),
    }"
  >
    <div
      class="map-kit-bottom-panel-gutter"
      @mousedown.prevent="startPanelResize"
      @dblclick="toggleFullscreen"
    >
      <div class="map-kit-bottom-panel-grip" />
    </div>

    <div class="map-kit-bottom-panel-header">
      <div class="map-kit-bottom-panel-header-main">
        <div class="map-kit-bottom-panel-dock-launchers">
          <button
            ref="filterUtilityLauncher"
            class="map-kit-bottom-panel-dock-toggle"
            :class="{ active: activeUtilityPanel === 'filters' }"
            aria-haspopup="dialog"
            :aria-expanded="activeUtilityPanel === 'filters'"
            @click="toggleUtilityPanel('filters')"
          >
            <span class="map-kit-bottom-panel-dock-toggle-icon">
              <FontAwesomeIcon icon="fas fa-filter" />
            </span>
            <span class="map-kit-bottom-panel-dock-toggle-label">{{ $t('filters.satellitesFilter') }}</span>
            <FontAwesomeIcon
              :icon="activeUtilityPanel === 'filters' ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"
              class="map-kit-bottom-panel-dock-toggle-arrow"
            />
          </button>

          <button
            v-if="mapViewMode === 'aoi'"
            ref="monitorUtilityLauncher"
            class="map-kit-bottom-panel-dock-toggle map-kit-bottom-panel-dock-toggle--primary"
            :class="{ active: activeUtilityPanel === 'monitor' }"
            aria-haspopup="dialog"
            :aria-expanded="activeUtilityPanel === 'monitor'"
            @click="toggleUtilityPanel('monitor')"
          >
            <span class="map-kit-bottom-panel-dock-toggle-icon">
              <FontAwesomeIcon :icon="monitorActionIcon" />
            </span>
            <span class="map-kit-bottom-panel-dock-toggle-label">{{ monitorActionLabel }}</span>
            <span v-if="areas.length > 0" class="map-kit-bottom-panel-dock-toggle-count">{{ areas.length }}</span>
            <FontAwesomeIcon
              :icon="activeUtilityPanel === 'monitor' ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"
              class="map-kit-bottom-panel-dock-toggle-arrow"
            />
          </button>
        </div>

        <div class="map-kit-bottom-panel-tabs">
          <button
            class="map-kit-bottom-panel-tab"
            :class="{ active: activeTab === 'graph' }"
            @click="activeTab = 'graph'"
          >
            <FontAwesomeIcon icon="fas fa-chart-area" />
            <span>{{ $t('timeline.title') }}</span>
          </button>
          <button
            class="map-kit-bottom-panel-tab"
            :class="{ active: activeTab === 'list' }"
            @click="activeTab = 'list'"
          >
            <FontAwesomeIcon icon="fas fa-list" />
            <span>{{ $t('list.title') }}</span>
          </button>
        </div>

        <div class="map-kit-bottom-panel-meta">
          <span class="map-kit-bottom-panel-count">{{ inRangePassCount }}</span>
          <span
            v-if="(mapViewMode === 'aoi' && hasAreas) || (mapViewMode === 'grid' && selectedHexCells.size > 0) || mapViewMode === 'swath'"
            class="map-kit-bottom-panel-status"
            v-tooltip.right="'Geostationary satellites monitor this area continuously, capturing data at regular intervals.'"
          >
            <FontAwesomeIcon icon="fas fa-satellite-dish" class="text-blue-500" />
            <span>Continuous Geostationary Coverage</span>
          </span>
        </div>
      </div>

      <div class="map-kit-bottom-panel-actions">
        <div v-if="activeTab === 'graph'" class="timeline-mode-selector">
          <button class="timeline-aoi-selector-btn" @click="modeDropdownOpen = !modeDropdownOpen">
            <FontAwesomeIcon icon="fas fa-chart-bar" />
            <span>{{ modeSelectorLabel }}</span>
            <FontAwesomeIcon :icon="modeDropdownOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="timeline-aoi-chevron" />
          </button>
          <div v-if="modeDropdownOpen" class="timeline-mode-selector-dropdown">
            <button
              v-for="opt in modeOptions"
              :key="opt.value"
              class="timeline-mode-selector-item"
              :class="{ active: graphMode === opt.value }"
              @click="graphMode = opt.value; modeDropdownOpen = false"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>

        <div class="cloud-filter-inline" v-tooltip.top="$t('list.maxCloudCover', { value: cloudCoverThreshold })">
          <FontAwesomeIcon icon="fas fa-cloud" class="cloud-filter-inline-icon" />
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            class="cloud-filter-inline-slider"
            :value="cloudCoverThreshold"
            @input="onCloudCoverSliderInput(($event.target as HTMLInputElement).value)"
          />
          <label class="cloud-filter-inline-value">
            <span class="cloud-filter-inline-value-prefix">≤</span>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              inputmode="numeric"
              class="cloud-filter-inline-value-input"
              :value="cloudCoverInputValue"
              :aria-label="$t('list.maxCloudCover', { value: cloudCoverThreshold })"
              @input="onCloudCoverValueInput(($event.target as HTMLInputElement).value)"
              @blur="commitCloudCoverValueInput"
              @keydown.enter.prevent="commitCloudCoverValueInput"
            />
            <span class="cloud-filter-inline-value-suffix">%</span>
          </label>
        </div>
        <Button
          v-tooltip.top="fullscreen ? $t('timeline.restore') : $t('timeline.maximize')"
          :icon="fullscreen ? 'fas fa-compress' : 'fas fa-expand'"
          severity="contrast"
          text
          size="small"
          @click="toggleFullscreen"
        />
        <Button
          v-tooltip.top="$t('timeline.close')"
          icon="fas fa-xmark"
          severity="contrast"
          text
          size="small"
          @click="$emit('close')"
        />
      </div>
    </div>

    <div class="map-kit-bottom-panel-body">
      <div class="map-kit-bottom-panel-shell">
        <div class="map-kit-bottom-panel-main">
          <MapListPanel v-if="activeTab === 'list'" embedded />
          <div v-else-if="mapViewMode === 'aoi' && !hasAreas" class="map-kit-bottom-panel-placeholder">
            {{ $t('timeline.uploadAoiTimeline') }}
          </div>
          <div v-else-if="mapViewMode === 'grid' && selectedHexCells.size === 0" class="map-kit-bottom-panel-placeholder">
            {{ $t('timeline.selectGridTimeline') }}
          </div>
          <div v-else class="map-kit-bottom-panel-graph">
            <div class="timeline-swim-layout">
              <div
            v-if="graphMode === 'grouped' || graphMode === 'sensor'"
            ref="swimLabelContainer"
            class="timeline-swim-labels"
            @scroll="onLabelScroll"
          >
            <div class="timeline-swim-label-header" :style="{ height: axisAreaHeight + 'px' }" />
            <div
              v-for="(lane, idx) in activeSwimLanes"
              :key="lane.name"
              class="timeline-swim-label"
              :class="{ 'timeline-swim-label--alt': idx % 2 === 1 }"
              :style="{ height: lane.rowH + 'px' }"
              :title="lane.name"
            >
              {{ lane.shortName }}
              <img v-if="lane.shortName === 'SAFIRE'" :src="ororaIconRed" class="timeline-sensor-ot-icon" alt="" />
            </div>
          </div>

          <div v-if="graphMode === 'coverage'" class="timeline-coverage-y-axis">
            <div class="timeline-coverage-y-header" :style="{ height: axisAreaHeight + 'px' }" />
            <div class="timeline-coverage-y-labels" :style="{ height: (graphHeight - axisAreaHeight) + 'px' }">
              <span v-for="tick in coverageYTicks" :key="tick.pct" class="timeline-coverage-y-label" :style="{ top: tick.cssTop }">
                {{ tick.pct }}%
              </span>
            </div>
          </div>

          <div ref="graphAreaContainer" class="timeline-graph-area">
            <div
              ref="graphScrollContainer"
              class="timeline-graph-scroll"
              :style="{ overflowY: graphMode === 'combined' || graphMode === 'coverage' ? 'hidden' : 'auto' }"
              @scroll="onGraphScroll"
            >
              <div class="timeline-axis-sticky" :style="{ height: axisAreaHeight + 'px' }">
                <svg :viewBox="`0 0 ${graphWidth} ${axisAreaHeight}`" preserveAspectRatio="none" style="overflow: visible">
                  <template v-for="tick in axisTicks" :key="'ax-' + tick.time">
                    <line
                      :x1="tick.x" :y1="axisAreaHeight"
                      :x2="tick.x" :y2="axisAreaHeight - (tick.type === '24h' ? 18 : tick.type === '12h' ? 14 : tick.type === '6h' ? 10 : 6)"
                      :class="'timeline-tick-mark timeline-tick-mark--' + tick.type"
                      :stroke-width="tick.type === '24h' ? 1.5 : tick.type === '12h' ? 1.25 : 1"
                      :stroke="tickMarkStroke(tick.type)"
                    />
                    <text
                      v-if="tick.showLabel"
                      :x="tick.x"
                      :y="axisAreaHeight - 24"
                      text-anchor="middle"
                      :class="'timeline-tick-label timeline-tick-label--' + tick.type"
                      :font-size="tick.type === '24h' ? 12 : tick.type === '12h' ? 11 : 10"
                      :font-weight="tick.type === '24h' ? 700 : tick.type === '12h' ? 500 : 400"
                      :fill="tickLabelFill"
                    >
                      {{ tick.label }}
                    </text>
                  </template>
                </svg>
              </div>

              <div
                ref="graphContainer"
                class="timeline-graph"
                :style="{
                  height: svgTotalHeight + 'px',
                }"
                @mousemove="onGraphMouseMove"
                @mouseleave="onGraphMouseLeave"
                @click="onGraphClick"
              >
                <svg class="timeline-svg" :viewBox="`0 0 ${graphWidth} ${svgTotalHeight}`" preserveAspectRatio="none">
                  <defs v-if="graphMode === 'coverage' && isColorblindFriendly">
                    <pattern
                      v-for="entry in coverageLegendEntries"
                      :id="entry.patternId"
                      :key="entry.patternId"
                      patternUnits="userSpaceOnUse"
                      width="8"
                      height="8"
                    >
                      <rect width="8" height="8" :fill="entry.color" />
                      <circle
                        v-if="entry.patternIndex === 6"
                        cx="2"
                        cy="2"
                        r="1.25"
                        :fill="getSensorPatternStroke(entry.color)"
                      />
                      <path
                        v-else
                        :d="getSensorPatternPath(entry.patternIndex)"
                        :stroke="getSensorPatternStroke(entry.color)"
                        stroke-width="1.4"
                        fill="none"
                      />
                    </pattern>
                  </defs>

                  <template v-if="graphMode === 'grouped' || graphMode === 'sensor'">
                    <rect
                      v-for="(lane, idx) in activeSwimLanes"
                      :key="'bg-' + lane.name"
                      :x="0"
                      :y="lane.yOffset"
                      :width="graphWidth"
                      :height="lane.rowH"
                      :class="idx % 2 === 1 ? 'timeline-lane-bg-alt' : undefined"
                      :fill="idx % 2 === 1 ? svgColors.laneBgAlt : 'transparent'"
                    />
                    <line
                      v-for="lane in activeSwimLanes"
                      :key="'div-' + lane.name"
                      :x1="0"
                      :y1="lane.yOffset + lane.rowH"
                      :x2="graphWidth"
                      :y2="lane.yOffset + lane.rowH"
                      class="timeline-lane-divider"
                      stroke-width="1"
                      :stroke="svgColors.laneDivider"
                    />
                  </template>

                  <rect v-if="graphMode === 'combined'" x="0" :y="0" :width="graphWidth" :height="contentTrackHeight" rx="3" class="timeline-track-bg" :fill="svgColors.trackBg" />

                  <template v-if="graphMode === 'coverage'">
                    <line
                      v-for="tick in coverageYTicks"
                      :key="'cg-' + tick.pct"
                      :x1="graphPadX"
                      :y1="tick.svgY"
                      :x2="graphPadX + usableWidth"
                      :y2="tick.svgY"
                      :class="['timeline-coverage-guide', tick.pct === 0 || tick.pct === 100 ? 'timeline-coverage-guide--edge' : 'timeline-coverage-guide--inner']"
                      :stroke="tick.pct === 0 || tick.pct === 100 ? svgColors.coverageGuideEdge : svgColors.coverageGuideInner"
                      :stroke-dasharray="tick.pct === 0 || tick.pct === 100 ? 'none' : '4,3'"
                    />
                    <template v-for="seg in coverageStackSegments" :key="'cov-' + seg.bucket.startMs + '-' + seg.sensorType">
                      <path
                        v-if="seg.isTop && seg.h >= 3"
                        :d="roundedTopPath(seg.x, seg.y, seg.w, seg.h, 2)"
                        :fill="seg.fill"
                        :opacity="seg.opacity"
                        class="timeline-coverage-bar"
                      />
                      <rect
                        v-else
                        :x="seg.x"
                        :y="seg.y"
                        :width="seg.w"
                        :height="seg.h"
                        :fill="seg.fill"
                        :opacity="seg.opacity"
                        class="timeline-coverage-bar"
                      />
                    </template>
                  </template>

                  <template v-if="graphMode !== 'coverage'">
                    <rect
                      v-for="bar in overpassBars"
                      :key="bar.pass.id"
                      :x="bar.x"
                      :y="bar.y"
                      :width="bar.w"
                      :height="bar.h"
                      rx="3"
                      :fill="getBarFill(bar)"
                      :stroke="getBarStroke(bar)"
                      :stroke-width="getBarStrokeWidth(bar)"
                      :class="graphMode === 'combined' ? 'timeline-bar timeline-bar--combined' : 'timeline-bar'"
                    />
                    <!-- Cloud cover overlay: semi-transparent white stripe at top of bar -->
                    <rect
                      v-for="bar in cloudOverlayBars"
                      :key="'cloud-' + bar.pass.id"
                      :x="bar.x"
                      :y="bar.y"
                      :width="bar.w"
                      :height="bar.cloudH"
                      rx="3"
                      :fill="bar.cloudFill"
                      class="timeline-cloud-overlay"
                    />
                    <!-- Delivery whiskers stay above bars so the delivery moment remains legible -->
                    <template v-for="w in deliveryWhiskers" :key="'dw-' + w.pass.id">
                      <path
                        v-if="isWhiskerHighlighted(w.pass)"
                        :d="w.path"
                        class="timeline-delivery-whisker timeline-delivery-whisker--highlight"
                        :stroke="getWhiskerHighlightStroke(w.pass)"
                        :stroke-width="getWhiskerHighlightStrokeWidth(w.pass)"
                        :stroke-dasharray="getWhiskerDasharray(w.pass)"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        :d="w.path"
                        class="timeline-delivery-whisker"
                        :stroke="getWhiskerStroke(w.pass)"
                        :stroke-width="getWhiskerStrokeWidth(w.pass)"
                        :stroke-dasharray="getWhiskerDasharray(w.pass)"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <line
                        v-if="isWhiskerHighlighted(w.pass)"
                        :x1="w.x2" :y1="w.tickY1" :x2="w.x2" :y2="w.tickY2"
                        class="timeline-delivery-whisker-tick timeline-delivery-whisker-tick--highlight"
                        :stroke="getWhiskerHighlightStroke(w.pass)"
                        :stroke-width="getWhiskerHighlightTickStrokeWidth(w.pass)"
                        stroke-linecap="round"
                      />
                      <line
                        :x1="w.x2" :y1="w.tickY1" :x2="w.x2" :y2="w.tickY2"
                        class="timeline-delivery-whisker-tick"
                        :stroke="getWhiskerTickStroke(w.pass)"
                        :stroke-width="getWhiskerTickStrokeWidth(w.pass)"
                        stroke-linecap="round"
                      />
                    </template>
                  </template>
                </svg>
              </div>
            </div>

            <div class="timeline-track-overlay" :style="{ top: axisAreaHeight + 'px', height: overlayTrackHeight + 'px' }">
              <svg class="timeline-track-overlay-svg" :viewBox="`0 0 ${graphWidth} ${overlayTrackHeight}`" preserveAspectRatio="none">
                <rect
                  :x="handleLeftPx"
                  :y="0"
                  :width="Math.max(0, handleRightPx - handleLeftPx)"
                  :height="overlayTrackHeight"
                  rx="3"
                  fill="rgba(3, 222, 204, 0.1)"
                />

                <template v-for="tick in axisTicks" :key="'ov-' + tick.time">
                  <line
                    :x1="tick.x" :y1="0"
                    :x2="tick.x" :y2="overlayTrackHeight"
                    :class="'timeline-tick-guide timeline-tick-guide--' + tick.type"
                    stroke-width="1"
                    stroke-dasharray="2,3"
                    :stroke="tickGuideStroke(tick.type)"
                  />
                </template>
              </svg>
            </div>

            <div class="timeline-handle-overlay" :style="{ top: axisAreaHeight + 'px', height: overlayTrackHeight + 'px' }">
              <div
                class="timeline-handle timeline-handle--left"
                :style="{ left: handleLeftPx + 'px' }"
                @mousedown.prevent="startDrag('left')"
              >
                <div class="timeline-handle-grip" />
              </div>
              <div
                class="timeline-handle timeline-handle--right"
                :style="{ left: handleRightPx + 'px' }"
                @mousedown.prevent="startDrag('right')"
              >
                <div class="timeline-handle-grip" />
              </div>
            </div>

            <div
              v-if="graphMode === 'coverage' && coverageLegendEntries.length > 0"
              class="timeline-coverage-legend"
              :style="{ top: (axisAreaHeight + 10) + 'px' }"
            >
              <span
                v-for="entry in coverageLegendEntries"
                :key="entry.sensorType"
                class="timeline-coverage-legend-item"
              >
                <span
                  class="timeline-coverage-legend-swatch"
                  :style="getSensorLegendSwatchStyle(entry)"
                />
                {{ entry.sensorType }}
                <img v-if="entry.sensorType === 'SAFIRE'" :src="ororaIconRed" class="timeline-sensor-ot-icon" alt="" />
              </span>
            </div>

            <div
              v-if="bottomMaskHeight > 0"
              class="timeline-bottom-mask"
              :style="{ height: bottomMaskHeight + 'px' }"
            />
          </div>
        </div>

        <Teleport to="body">
          <div
            v-if="hoveredBar && graphMode !== 'coverage'"
            class="timeline-tooltip"
            :style="{ left: tooltipScreenX + 'px', top: tooltipScreenY + 'px' }"
          >
            <strong>{{ hoveredBar.pass.name }}</strong>
            <table class="timeline-tooltip-table">
              <tbody>
                <tr><td>{{ $t('popup.start') }}</td><td>{{ formatDateTime(hoveredBar.pass.start_time) }}</td></tr>
                <tr><td>{{ $t('popup.end') }}</td><td>{{ formatDateTime(hoveredBar.pass.end_time) }}</td></tr>
                <tr v-if="hoveredBar.pass.direction"><td>{{ $t('popup.direction') }}</td><td>{{ hoveredBar.pass.direction === 'ASCENDING' ? $t('popup.ascending') : $t('popup.descending') }}</td></tr>
                <tr v-if="hoveredBar.pass.sensor_type"><td>{{ $t('popup.sensor') }}</td><td>{{ hoveredBar.pass.sensor_type }}</td></tr>
                <tr v-if="hoveredBar.pass.spatial_res_m != null"><td>{{ $t('popup.resolution') }}</td><td>{{ hoveredBar.pass.spatial_res_m }}m</td></tr>
                <tr>
                  <td>{{ $t('popup.cloudCoverForecast') }}</td>
                  <td>{{ hoveredBar.pass.cloud_cover_pct != null ? `${hoveredBar.pass.cloud_cover_pct}%` : $t('popup.notCalculated') }}</td>
                </tr>
                <tr v-if="hoveredBar.pass.is_daytime != null"><td>{{ $t('popup.daylight') }}</td><td>{{ hoveredBar.pass.is_daytime ? $t('popup.day') : $t('popup.night') }}</td></tr>
                <tr v-if="getHoveredLatency() != null"><td>{{ $t('popup.expectedLatency') }}</td><td>~{{ Math.round(getHoveredLatency()!) }} min</td></tr>
                <tr v-if="getHoveredDelivery()"><td>{{ $t('popup.expectedDelivery') }}</td><td>{{ formatDateTime(getHoveredDelivery()!) }}</td></tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="hoveredCoverageBucket && graphMode === 'coverage'"
            class="timeline-tooltip"
            :style="{ left: tooltipScreenX + 'px', top: tooltipScreenY + 'px' }"
          >
            <strong>{{ $t('popup.coverage', { pct: Math.round(hoveredCoverageBucket.pct) }) }}</strong>
            <table class="timeline-tooltip-table">
              <tbody>
                <tr><td>{{ $t('popup.from') }}</td><td>{{ formatDateTime(new Date(hoveredCoverageBucket.startMs).toISOString()) }}</td></tr>
                <tr><td>{{ $t('popup.to') }}</td><td>{{ formatDateTime(new Date(hoveredCoverageBucket.endMs).toISOString()) }}</td></tr>
                <tr>
                  <td>{{ $t('popup.cloudCoverForecast') }}</td>
                  <td>{{ hoveredCoverageBucket.cloudPct != null ? `${Math.round(hoveredCoverageBucket.cloudPct)}%` : $t('popup.notCalculated') }}</td>
                </tr>
                <template v-if="hoveredCoverageBucket.sensorBreakdown.length > 0">
                  <tr class="timeline-tooltip-section-header">
                    <td colspan="2">By sensor</td>
                  </tr>
                  <tr
                    v-for="seg in hoveredCoverageBucket.sensorBreakdown"
                    :key="'sensor-' + seg.sensorType"
                  >
                    <td>
                      <span class="timeline-tooltip-sensor-row">
                        <span
                          class="timeline-tooltip-sensor-swatch"
                          :style="{ background: getSensorColor(seg.sensorType) }"
                        />
                        <span>{{ seg.sensorType }}</span>
                        <img v-if="seg.sensorType === 'SAFIRE'" :src="ororaIconRed" class="timeline-sensor-ot-icon" alt="" />
                      </span>
                    </td>
                    <td>{{ Math.round(seg.pct) }}%</td>
                  </tr>
                </template>
                <template v-if="hoveredCoverageBucket.aoiPcts.length > 1">
                  <tr class="timeline-tooltip-section-header">
                    <td colspan="2">By area</td>
                  </tr>
                  <tr
                    v-for="(pct, idx) in hoveredCoverageBucket.aoiPcts"
                    :key="'aoi-pct-' + idx"
                  >
                    <td>{{ coverageTooltipAoiNames[idx] }}</td>
                    <td>{{ Math.round(pct) }}%</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </Teleport>
      </div>
    </div>
  </div>
  </div>
  </div>

  <Teleport to="body">
    <div
      v-if="visible && activeUtilityPanel"
      ref="utilityFloatingPanel"
      class="map-kit-bottom-panel-utility-float"
      :class="[
        `map-kit-bottom-panel-utility-float--${activeUtilityPanel}`,
        { 'map-kit-bottom-panel-utility-float--compact': isUtilityPanelCompact },
      ]"
      :style="utilityFloatingPanelStyle"
      role="dialog"
      tabindex="0"
      :aria-label="utilityPanelTitle"
      @click.stop
    >
      <div class="map-kit-bottom-panel-utility-float-header">
        <div class="map-kit-bottom-panel-utility-float-heading">
          <strong>{{ utilityPanelTitle }}</strong>
          <span
            v-if="activeUtilityPanel === 'monitor' && areas.length > 0"
            class="map-kit-bottom-panel-utility-float-badge"
          >
            {{ areas.length }}
          </span>
        </div>
        <Button
          icon="fas fa-xmark"
          severity="contrast"
          text
          size="small"
          aria-label="Close utility panel"
          @click="activeUtilityPanel = null"
        />
      </div>

      <div class="map-kit-bottom-panel-utility-float-body">
        <MapFilterPanel v-if="activeUtilityPanel === 'filters'" embedded />
        <MapMonitorPanel v-else embedded />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Button from 'primevue/button';
import { useFilterStore, type VisiblePass } from '@/composables/useFilterStore';
import { useMonitoredAreasStore } from '@/composables/useMonitoredAreasStore';
import { parseUtcEpochMs, useDateTimeSettings } from '@/composables/useDateTimeSettings';
import { getTimingColorForHours, NO_UPCOMING_HOURS } from '@/composables/useTimingColorScale';
import { isColorblindFriendly } from '@/composables/useAccessibilitySettings';
import {
  useSatelliteLatency,
  computeExpectedDeliveryForPass,
  getExpectedLatencyMinutesForPass,
} from '@/composables/useSatelliteLatency';
import ororaIconRed from '@/assets/img/orora_icon_red.svg';
import {
  collapseOroraConstellationPasses,
  getPrimaryPassId,
  groupedPassContainsRawId,
  type GroupedVisiblePass,
} from '@/composables/useConstellationGrouping';
import { useAoiCoverageBuckets, type CoverageBucket } from '@/composables/useAoiCoverageBuckets';
import { useColorScheme } from '@/composables/useColorScheme';
import MapFilterPanel from './MapFilterPanel.vue';
import MapListPanel from './MapListPanel.vue';
import MapMonitorPanel from './MapMonitorPanel.vue';

const props = withDefaults(defineProps<{
  visible: boolean;
  aoiMonitorAutoOpenVersion?: number;
}>(), {
  aoiMonitorAutoOpenVersion: 0,
});

defineEmits<{ (e: 'close'): void }>();

const {
  swathGraphPasses,
  timeRange,
  selectedPassId,
  hoveredPassId,
  setSelectedPass,
  setHoveredPass,
  mapViewMode,
  hexTimingSelectedPassesAll,
  ororaConstellationExpanded,
  selectedHexCells,
  cloudCoverThreshold,
  passWithinCloudThreshold,
} = useFilterStore();

const { t } = useI18n();
const { aoiPassesAll, hasAreas, areas } = useMonitoredAreasStore();
const { formatDateTime, formatDateShort, formatTimeShort, getHourInEffectiveTimeZone } = useDateTimeSettings();
const { coverageBuckets } = useAoiCoverageBuckets();
const { realColorScheme } = useColorScheme();
const tickLabelFill = computed(() => realColorScheme.value === 'dark' ? 'rgba(250,249,247,1)' : 'rgba(0,0,0,1)');

// Inline SVG colors for html-to-image compatibility (CSS classes aren't reliably captured)
const isDarkMode = computed(() => realColorScheme.value === 'dark');
const svgColors = computed(() => {
  const dark = isDarkMode.value;
  return {
    laneBgAlt: dark ? 'rgba(250,249,247,0.045)' : 'rgba(0,0,0,0.05)',
    laneDivider: dark ? 'rgba(250,249,247,0.1)' : 'rgba(0,0,0,0.12)',
    trackBg: dark ? 'rgba(250,249,247,0.06)' : 'rgba(0,0,0,0.06)',
    tickGuide24h: dark ? 'rgba(250,249,247,0.18)' : 'rgba(0,0,0,0.18)',
    tickGuide12h: dark ? 'rgba(250,249,247,0.1)' : 'rgba(0,0,0,0.1)',
    tickGuide6h: dark ? 'rgba(250,249,247,0.06)' : 'rgba(0,0,0,0.06)',
    tickGuide3h: dark ? 'rgba(250,249,247,0.03)' : 'rgba(0,0,0,0.03)',
    tickMark24h: dark ? 'rgba(250,249,247,0.85)' : 'rgba(0,0,0,0.85)',
    tickMark12h: dark ? 'rgba(250,249,247,0.80)' : 'rgba(0,0,0,0.80)',
    tickMark6h: dark ? 'rgba(250,249,247,0.70)' : 'rgba(0,0,0,0.70)',
    tickMark3h: dark ? 'rgba(250,249,247,0.60)' : 'rgba(0,0,0,0.60)',
    whisker: dark ? 'rgba(250,249,247,0.5)' : 'rgba(12,35,43,0.45)',
    whiskerTick: dark ? 'rgba(250,249,247,0.65)' : 'rgba(12,35,43,0.6)',
    coverageGuideEdge: dark ? 'rgba(250,249,247,0.35)' : 'rgba(0,0,0,0.3)',
    coverageGuideInner: dark ? 'rgba(250,249,247,0.18)' : 'rgba(0,0,0,0.14)',
  };
});

function tickGuideStroke(type: string): string {
  const c = svgColors.value;
  if (type === '24h') return c.tickGuide24h;
  if (type === '12h') return c.tickGuide12h;
  if (type === '6h') return c.tickGuide6h;
  return c.tickGuide3h;
}

function tickMarkStroke(type: string): string {
  const c = svgColors.value;
  if (type === '24h') return c.tickMark24h;
  if (type === '12h') return c.tickMark12h;
  if (type === '6h') return c.tickMark6h;
  return c.tickMark3h;
}

const graphPasses = computed<GroupedVisiblePass[]>(() => {
  const [rangeStart, rangeEnd] = timeRange.value;
  let source: VisiblePass[] = [];
  if (mapViewMode.value === 'aoi') {
    source = aoiPassesAll.value;
  } else if (mapViewMode.value === 'grid') {
    source = hexTimingSelectedPassesAll.value;
  } else {
    source = swathGraphPasses.value;
  }
  const inRange = source
    .filter((pass) => {
      if (!passWithinCloudThreshold(pass)) return false;
      const startMs = parseUtcEpochMs(pass.start_time);
      const endMs = parseUtcEpochMs(pass.end_time);
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return false;
      return endMs >= rangeStart && startMs <= rangeEnd;
    })
    .sort((a, b) => parseUtcEpochMs(a.start_time) - parseUtcEpochMs(b.start_time));
  return collapseOroraConstellationPasses(inRange, ororaConstellationExpanded.value, {
    timeBucketReferenceMs: timeRange.value[0],
  });
});

const modeDropdownOpen = ref(false);
const activeTab = ref<'graph' | 'list'>('graph');
const activeUtilityPanel = ref<'filters' | 'monitor' | null>(null);
const hasAutoOpenedAoiMonitorPanel = ref(false);
const bottomPanelRoot = ref<HTMLElement | null>(null);
const filterUtilityLauncher = ref<HTMLElement | null>(null);
const monitorUtilityLauncher = ref<HTMLElement | null>(null);
const utilityFloatingPanel = ref<HTMLElement | null>(null);

interface UtilityPanelLayout {
  bottom: number;
  caretLeft: number;
  left: number;
  maxHeight: number;
  width: number;
}

const utilityPanelLayout = ref<UtilityPanelLayout>({
  bottom: 0,
  caretLeft: 32,
  left: 0,
  maxHeight: 420,
  width: 440,
});
let utilityPanelResizeObserver: ResizeObserver | null = null;
let observedUtilityPanelElements: HTMLElement[] = [];

if (mapViewMode.value === 'aoi') {
  activeTab.value = 'graph';
  activeUtilityPanel.value = 'monitor';
  hasAutoOpenedAoiMonitorPanel.value = true;
}

let autoOpenMonitorTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Open the add-area panel, deferred past the click that asked for it.
 *
 * Switching to the AOI view is itself a click, and `onClickOutsideUtilityPanel` is a
 * document-level listener. The browser runs a microtask checkpoint between event
 * listeners, so Vue flushes this watcher — opening the panel — while the click is
 * still bubbling; the document listener then sees an open panel and a target outside
 * it, and closes it again. Deferring to a macrotask puts the open after the event has
 * finished, which is why the mount-time open above has always worked and this path
 * never did.
 */
function autoOpenMonitorPanel(): void {
  if (autoOpenMonitorTimer) clearTimeout(autoOpenMonitorTimer);
  autoOpenMonitorTimer = setTimeout(() => {
    autoOpenMonitorTimer = null;
    if (mapViewMode.value !== 'aoi') return;
    activeTab.value = 'graph';
    activeUtilityPanel.value = 'monitor';
    hasAutoOpenedAoiMonitorPanel.value = true;
  }, 0);
}

const modeOptions = computed(() => {
  const opts: { value: typeof graphMode.value; label: string }[] = [
    { value: 'grouped', label: t('timeline.bySatellite') },
    { value: 'sensor', label: t('timeline.bySensor') },
    { value: 'combined', label: t('timeline.allSatellites') },
  ];
  if (mapViewMode.value === 'aoi') {
    opts.push({ value: 'coverage', label: t('timeline.coveragePct') });
  }
  return opts;
});

const modeSelectorLabel = computed(() =>
  modeOptions.value.find((o) => o.value === graphMode.value)?.label ?? graphMode.value,
);

const monitorActionLabel = computed(() =>
  areas.value.length > 0 ? t('toolbar.monitoredAreas') : t('monitor.addArea'),
);

const monitorActionIcon = computed(() =>
  areas.value.length > 0 ? 'fas fa-object-group' : 'fas fa-plus',
);

const cloudCoverInputValue = ref(String(cloudCoverThreshold.value));

const utilityPanelTitle = computed(() =>
  activeUtilityPanel.value === 'monitor' ? monitorActionLabel.value : t('filters.satellitesFilter'),
);
const isUtilityPanelCompact = computed(() =>
  utilityPanelLayout.value.width < 420 || utilityPanelLayout.value.maxHeight < 360,
);
const utilityFloatingPanelStyle = computed<Record<string, string>>(() => ({
  bottom: `${utilityPanelLayout.value.bottom}px`,
  left: `${utilityPanelLayout.value.left}px`,
  maxHeight: `${utilityPanelLayout.value.maxHeight}px`,
  width: `${utilityPanelLayout.value.width}px`,
  '--mk-bottom-panel-utility-caret-left': `${utilityPanelLayout.value.caretLeft}px`,
}));

function clampCloudCoverThreshold(value: number): number {
  return Math.min(100, Math.max(0, Math.round(value)));
}

function onCloudCoverSliderInput(rawValue: string): void {
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return;
  cloudCoverThreshold.value = clampCloudCoverThreshold(parsed);
}

function onCloudCoverValueInput(rawValue: string): void {
  cloudCoverInputValue.value = rawValue;
  if (rawValue.trim() === '') return;
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return;
  cloudCoverThreshold.value = clampCloudCoverThreshold(parsed);
}

function commitCloudCoverValueInput(): void {
  const parsed = Number(cloudCoverInputValue.value);
  if (Number.isFinite(parsed)) {
    cloudCoverThreshold.value = clampCloudCoverThreshold(parsed);
  }
  cloudCoverInputValue.value = String(cloudCoverThreshold.value);
}

function clampToRange(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function getUtilitySurface(): HTMLElement | null {
  return bottomPanelRoot.value?.closest('.map-kit-center') as HTMLElement | null;
}

function getActiveUtilityLauncher(): HTMLElement | null {
  if (activeUtilityPanel.value === 'filters') return filterUtilityLauncher.value;
  if (activeUtilityPanel.value === 'monitor') return monitorUtilityLauncher.value;
  return null;
}

function getUtilityPanelWidth(panel: 'filters' | 'monitor'): number {
  return panel === 'monitor' ? 640 : 460;
}

function getUtilityPanelMinWidth(panel: 'filters' | 'monitor'): number {
  return panel === 'monitor' ? 360 : 320;
}

function updateUtilityPanelLayout() {
  if (!props.visible || !activeUtilityPanel.value) return;

  const launcherRect = getActiveUtilityLauncher()?.getBoundingClientRect();
  const surfaceRect = getUtilitySurface()?.getBoundingClientRect();
  if (!launcherRect || !surfaceRect) return;

  const safeInset = 12;
  const floatingGap = 10;
  const availableWidth = Math.max(0, Math.floor(surfaceRect.width - safeInset * 2));
  if (availableWidth <= 0) return;

  const preferredWidth = getUtilityPanelWidth(activeUtilityPanel.value);
  const minWidth = getUtilityPanelMinWidth(activeUtilityPanel.value);
  const width = availableWidth < minWidth
    ? availableWidth
    : Math.min(preferredWidth, availableWidth);
  const maxHeight = Math.max(0, Math.floor(launcherRect.top - surfaceRect.top - safeInset - floatingGap));
  const leftMin = Math.floor(surfaceRect.left + safeInset);
  const leftMax = Math.floor(surfaceRect.right - safeInset - width);
  const left = leftMax >= leftMin ? clampToRange(Math.floor(launcherRect.left), leftMin, leftMax) : leftMin;
  const bottom = Math.max(0, Math.floor(window.innerHeight - launcherRect.top + floatingGap));
  const caretLeft = clampToRange(
    Math.round(launcherRect.left + (launcherRect.width / 2) - left),
    28,
    Math.max(28, width - 28),
  );

  utilityPanelLayout.value = {
    bottom,
    caretLeft,
    left,
    maxHeight,
    width,
  };
}

function syncUtilityPanelObservers() {
  if (typeof ResizeObserver === 'undefined') return;

  if (!utilityPanelResizeObserver) {
    utilityPanelResizeObserver = new ResizeObserver(() => {
      updateUtilityPanelLayout();
    });
  }

  const nextElements = props.visible && activeUtilityPanel.value
    ? [bottomPanelRoot.value, getUtilitySurface(), getActiveUtilityLauncher()].filter(
      (value): value is HTMLElement => value instanceof HTMLElement,
    )
    : [];

  for (const element of observedUtilityPanelElements) {
    if (!nextElements.includes(element)) {
      utilityPanelResizeObserver.unobserve(element);
    }
  }

  for (const element of nextElements) {
    if (!observedUtilityPanelElements.includes(element)) {
      utilityPanelResizeObserver.observe(element);
    }
  }

  observedUtilityPanelElements = nextElements;
}

function toggleUtilityPanel(panel: 'filters' | 'monitor') {
  activeUtilityPanel.value = activeUtilityPanel.value === panel ? null : panel;
}

function onClickOutsideModeDropdown(e: MouseEvent) {
  const target = e.target as HTMLElement;
  if (!target.closest('.timeline-mode-selector')) modeDropdownOpen.value = false;
}
function onClickOutsideUtilityPanel(e: MouseEvent) {
  if (!activeUtilityPanel.value) return;

  const target = e.target as HTMLElement | null;
  if (!target) return;
  if (utilityFloatingPanel.value?.contains(target)) return;
  if (filterUtilityLauncher.value?.contains(target)) return;
  if (monitorUtilityLauncher.value?.contains(target)) return;

  activeUtilityPanel.value = null;
}

function onUtilityPanelViewportResize() {
  updateUtilityPanelLayout();
}

function onUtilityPanelEscape(e: KeyboardEvent) {
  if (e.key !== 'Escape' || !activeUtilityPanel.value) return;
  activeUtilityPanel.value = null;
}

watch(modeDropdownOpen, (open) => {
  if (open) document.addEventListener('click', onClickOutsideModeDropdown, { capture: true });
  else document.removeEventListener('click', onClickOutsideModeDropdown, { capture: true });
  if (open) activeUtilityPanel.value = null;
});
watch(cloudCoverThreshold, (value) => {
  if (cloudCoverInputValue.value !== String(value)) {
    cloudCoverInputValue.value = String(value);
  }
});
watch(activeUtilityPanel, (panel) => {
  syncUtilityPanelObservers();

  if (!panel || !props.visible) return;

  modeDropdownOpen.value = false;
  nextTick(() => {
    updateUtilityPanelLayout();
    requestAnimationFrame(() => {
      updateUtilityPanelLayout();
      utilityFloatingPanel.value?.focus();
    });
  });
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutsideModeDropdown, { capture: true });
  document.removeEventListener('click', onClickOutsideUtilityPanel);
  document.removeEventListener('keydown', onUtilityPanelEscape);
  window.removeEventListener('resize', onUtilityPanelViewportResize);
  window.visualViewport?.removeEventListener('resize', onUtilityPanelViewportResize);
  utilityPanelResizeObserver?.disconnect();
  utilityPanelResizeObserver = null;
  observedUtilityPanelElements = [];
});

const inRangePassCount = computed(() => {
  const [rangeStart, rangeEnd] = timeRange.value;
  return graphPasses.value.filter((pass) => {
    const startMs = parseUtcEpochMs(pass.start_time);
    const endMs = parseUtcEpochMs(pass.end_time);
    return endMs >= rangeStart && startMs <= rangeEnd;
  }).length;
});

type GraphMode = 'combined' | 'grouped' | 'sensor' | 'coverage';
const GRAPH_MODE_STORAGE_KEY = 'overpassPlanner.graphMode';
const VALID_GRAPH_MODES: GraphMode[] = ['combined', 'grouped', 'sensor', 'coverage'];

function loadPersistedGraphMode(): GraphMode {
  try {
    const stored = localStorage.getItem(GRAPH_MODE_STORAGE_KEY);
    if (stored && VALID_GRAPH_MODES.includes(stored as GraphMode)) return stored as GraphMode;
  } catch { /* ignore */ }
  return 'grouped';
}

const persistedGraphMode = loadPersistedGraphMode();
const graphMode = ref<GraphMode>(
  persistedGraphMode === 'coverage' && mapViewMode.value !== 'aoi' ? 'grouped' : persistedGraphMode,
);
const fullscreen = ref(false);

const toggleFullscreen = () => {
  fullscreen.value = !fullscreen.value;
};

const panelHeight = ref(250);
const MIN_PANEL = 120;

function startPanelResize(e: MouseEvent) {
  const startY = e.clientY;
  const startH = panelHeight.value;

  const onMove = (ev: MouseEvent) => {
    const maxH = window.innerHeight * 0.42;
    const delta = startY - ev.clientY;
    panelHeight.value = Math.max(MIN_PANEL, Math.min(maxH, startH + delta));
  };

  const onUp = () => {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
  };

  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
}

function selectPass(pass: GroupedVisiblePass) {
  const targetId = getPrimaryPassId(pass);
  if (groupedPassContainsRawId(pass, selectedPassId.value)) {
    setSelectedPass(null);
    return;
  }
  setSelectedPass(targetId);
}

const graphContainer = ref<HTMLDivElement | null>(null);
const graphScrollContainer = ref<HTMLDivElement | null>(null);
const graphAreaContainer = ref<HTMLDivElement | null>(null);
const swimLabelContainer = ref<HTMLDivElement | null>(null);
const graphWidth = ref(800);
const graphHeight = ref(300);

const axisAreaHeight = 44;
const barHeight = 18;
const graphPadX = 14;
const MIN_LANE_HEIGHT = 28;
const MIN_BAR_WIDTH = 8;
const HIT_PAD_X = 4;
const HIT_PAD_Y = 4;

const usableWidth = computed(() => Math.max(1, graphWidth.value - 2 * graphPadX));

const contentTrackHeight = computed(() => {
  if ((graphMode.value === 'grouped' || graphMode.value === 'sensor') && activeSwimLanes.value.length) {
    const total = activeSwimLanes.value.reduce((sum, l) => sum + l.rowH, 0);
    return total;
  }
  // Combined / coverage modes fit inside the visible graph viewport (no vertical scrolling).
  const available = graphHeight.value - axisAreaHeight - 6;
  return Math.max(40, available);
});

const overlayTrackHeight = computed(() => {
  if (graphMode.value === 'combined' || graphMode.value === 'coverage') {
    return contentTrackHeight.value;
  }
  const viewportLaneHeight = graphHeight.value - axisAreaHeight - 6;
  return Math.max(40, viewportLaneHeight);
});

const bottomMaskHeight = computed(() => {
  const viewportLaneHeight = Math.max(0, graphHeight.value - axisAreaHeight);
  return Math.max(0, viewportLaneHeight - overlayTrackHeight.value);
});

const svgTotalHeight = computed(() => contentTrackHeight.value);

// ── Coverage % graph ──────────────────────────────────────────────────
const COVERAGE_PAD_TOP = 4;
const COVERAGE_PAD_BOTTOM = 4;

const coverageChartH = computed(() => contentTrackHeight.value);

const COVERAGE_Y_STEPS = [0, 25, 50, 75, 100];

const coverageYTicks = computed(() => {
  const drawH = coverageChartH.value - COVERAGE_PAD_TOP - COVERAGE_PAD_BOTTOM;
  if (drawH <= 0) return [];
  return COVERAGE_Y_STEPS.map((pct) => ({
    pct,
    cssTop: `${COVERAGE_PAD_TOP + drawH * (1 - pct / 100)}px`,
    svgY: COVERAGE_PAD_TOP + drawH * (1 - pct / 100),
  }));
});

// ── Sensor type color palette ──────────────────────────────────────────
// Keys are lowercase substrings of SAT_METADATA sensor_type values

const SENSOR_COLOR_PALETTE: [string, string][] = [
  ['safire', '#ff4724'],   // OroraTech thermal
  ['modis',  '#fb923c'],   // AQUA / TERRA
  ['viirs',  '#fbbf24'],   // NOAA/Suomi JPSS
  ['msi',    '#4ade80'],   // Sentinel-2 multispectral
  ['slstr',  '#6366f1'],   // Sentinel-3 thermal
  ['oli',    '#a3e635'],   // Landsat
  ['mersi',  '#2dd4bf'],   // FengYun
  ['sgli',   '#60a5fa'],   // GCOM-C
  ['avhrr',  '#c084fc'],   // MetOp
];

const COLORBLIND_SENSOR_PALETTE: [string, string][] = [
  ['safire', '#D55E00'],   // OroraTech thermal
  ['modis',  '#E69F00'],   // AQUA / TERRA
  ['viirs',  '#F0E442'],   // NOAA/Suomi JPSS
  ['msi',    '#009E73'],   // Sentinel-2 multispectral
  ['slstr',  '#0072B2'],   // Sentinel-3 thermal
  ['oli',    '#56B4E9'],   // Landsat
  ['mersi',  '#CC79A7'],   // FengYun
  ['sgli',   '#666666'],   // GCOM-C
  ['avhrr',  '#999999'],   // MetOp
];

const SENSOR_FALLBACK_COLORS = [
  '#06b6d4', '#6366f1', '#ec4899', '#f97316', '#10b981',
  '#a855f7', '#65a30d', '#0ea5e9', '#d946ef', '#0891b2',
];
const COLORBLIND_SENSOR_FALLBACK_COLORS = COLORBLIND_SENSOR_PALETTE.map(([, color]) => color);
const _sensorColorCache = new Map<string, string>();
const SENSOR_PATTERN_PATHS = [
  'M 2 0 V 8',
  'M 0 2 H 8',
  'M -2 8 L 8 -2 M 2 10 L 10 2',
  'M -2 0 L 8 10 M 2 -2 L 10 6',
  'M 2 0 V 8 M 0 2 H 8',
  'M -2 8 L 8 -2 M 2 10 L 10 2 M -2 0 L 8 10 M 2 -2 L 10 6',
  '',
  'M 2 0 V 8 M 6 0 V 8',
  'M 0 2 H 8 M 0 6 H 8',
] as const;

function getSensorColor(sensorType: string): string {
  const paletteKey = isColorblindFriendly.value ? 'colorblind' : 'default';
  const cacheKey = `${paletteKey}:${sensorType}`;
  if (_sensorColorCache.has(cacheKey)) return _sensorColorCache.get(cacheKey)!;
  const lower = sensorType.toLowerCase();
  if (isColorblindFriendly.value) {
    for (const [key, color] of COLORBLIND_SENSOR_PALETTE) {
      if (lower.includes(key)) { _sensorColorCache.set(cacheKey, color); return color; }
    }
  }
  for (const [key, color] of SENSOR_COLOR_PALETTE) {
    if (lower.includes(key)) { _sensorColorCache.set(cacheKey, color); return color; }
  }
  const fallbackColors = isColorblindFriendly.value
    ? COLORBLIND_SENSOR_FALLBACK_COLORS
    : SENSOR_FALLBACK_COLORS;
  const idx = [...sensorType].reduce((acc, c) => acc + c.charCodeAt(0), 0) % fallbackColors.length;
  const color = fallbackColors[idx];
  _sensorColorCache.set(cacheKey, color);
  return color;
}

function hashSensorType(sensorType: string): number {
  let hash = 0;
  for (const char of sensorType.toLowerCase()) {
    hash = ((hash * 31) + char.charCodeAt(0)) >>> 0;
  }
  return hash;
}

function getSensorPatternIndex(sensorType: string): number {
  const lower = sensorType.toLowerCase();
  const knownIndex = COLORBLIND_SENSOR_PALETTE.findIndex(([key]) => lower.includes(key));
  return knownIndex >= 0 ? knownIndex : hashSensorType(sensorType) % SENSOR_PATTERN_PATHS.length;
}

function getSensorPatternId(sensorType: string): string {
  return `coverage-sensor-pattern-${hashSensorType(sensorType).toString(36)}`;
}

function getSensorPatternPath(patternIndex: number): string {
  return SENSOR_PATTERN_PATHS[patternIndex] ?? SENSOR_PATTERN_PATHS[0];
}

function getSensorPatternStroke(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.58 ? 'rgba(12, 35, 43, 0.78)' : 'rgba(255, 255, 255, 0.9)';
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function roundedTopPath(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.min(r, w / 2, h / 2);
  return `M ${x + rr},${y} L ${x + w - rr},${y} Q ${x + w},${y} ${x + w},${y + rr} L ${x + w},${y + h} L ${x},${y + h} L ${x},${y + rr} Q ${x},${y} ${x + rr},${y} Z`;
}

interface CoverageStackSegment {
  bucket: CoverageBucket;
  sensorType: string;
  fill: string;
  opacity: number;
  color: string; // base hex for legend
  patternId: string;
  patternIndex: number;
  x: number;
  y: number;
  w: number;
  h: number;
  isTop: boolean;
}

const coverageStackSegments = computed<CoverageStackSegment[]>(() => {
  if (graphMode.value !== 'coverage') return [];
  const bkts = coverageBuckets.value;
  if (!bkts.length) return [];

  const chartH = coverageChartH.value;
  const drawH = chartH - COVERAGE_PAD_TOP - COVERAGE_PAD_BOTTOM;
  if (drawH <= 0) return [];

  const segments: CoverageStackSegment[] = [];

  for (const bucket of bkts) {
    const x1 = timeToPx(bucket.startMs);
    const x2 = timeToPx(bucket.endMs);
    const w = Math.max(1, Math.abs(x2 - x1) - 1);
    const x = Math.min(x1, x2);
    const inRange = bucket.endMs > timeRange.value[0] && bucket.startMs < timeRange.value[1];
    const alpha = inRange ? 0.82 : 0.28;

    if (bucket.pct <= 0 || !bucket.sensorBreakdown.length) {
      // Empty bucket or no breakdown — render a minimal placeholder bar
      const baseColor = bucket.sensorBreakdown.length ? getSensorColor(bucket.sensorBreakdown[0].sensorType) : '#3c414b';
      segments.push({
        bucket, sensorType: '', fill: `rgba(60, 65, 75, ${alpha * 0.35})`,
        opacity: 1, color: baseColor, patternId: '', patternIndex: 0,
        x, y: COVERAGE_PAD_TOP + drawH - 1, w, h: 1, isTop: true,
      });
      continue;
    }

    // Stack segments bottom-up; breakdown is sorted largest-first
    let currentY = COVERAGE_PAD_TOP + drawH;
    for (let si = 0; si < bucket.sensorBreakdown.length; si++) {
      const { sensorType, pct } = bucket.sensorBreakdown[si];
      const segH = Math.max(1, (pct / 100) * drawH);
      currentY -= segH;
      const isTop = si === bucket.sensorBreakdown.length - 1;
      const baseColor = getSensorColor(sensorType);
      const usePattern = isColorblindFriendly.value;
      const patternId = getSensorPatternId(sensorType);
      segments.push({
        bucket, sensorType,
        fill: usePattern ? `url(#${patternId})` : hexToRgba(baseColor, alpha),
        opacity: usePattern ? alpha : 1,
        color: baseColor,
        patternId,
        patternIndex: getSensorPatternIndex(sensorType),
        x, y: currentY, w, h: segH, isTop,
      });
    }
  }

  return segments;
});

interface CoverageLegendEntry {
  sensorType: string;
  color: string;
  patternId: string;
  patternIndex: number;
}

const coverageLegendEntries = computed<CoverageLegendEntry[]>(() => {
  if (graphMode.value !== 'coverage') return [];
  const seen = new Map<string, CoverageLegendEntry>();
  for (const seg of coverageStackSegments.value) {
    if (seg.sensorType && !seen.has(seg.sensorType)) {
      seen.set(seg.sensorType, {
        sensorType: seg.sensorType,
        color: seg.color,
        patternId: seg.patternId,
        patternIndex: seg.patternIndex,
      });
    }
  }
  return [...seen.values()];
});

function getSensorLegendSwatchStyle(entry: CoverageLegendEntry): Record<string, string> {
  if (!isColorblindFriendly.value) return { background: entry.color };

  const stroke = getSensorPatternStroke(entry.color);
  const backgrounds = [
    `repeating-linear-gradient(90deg, transparent 0 3px, ${stroke} 3px 4px)`,
    `repeating-linear-gradient(0deg, transparent 0 3px, ${stroke} 3px 4px)`,
    `repeating-linear-gradient(45deg, transparent 0 3px, ${stroke} 3px 4px)`,
    `repeating-linear-gradient(-45deg, transparent 0 3px, ${stroke} 3px 4px)`,
    `repeating-linear-gradient(90deg, transparent 0 3px, ${stroke} 3px 4px), repeating-linear-gradient(0deg, transparent 0 3px, ${stroke} 3px 4px)`,
    `repeating-linear-gradient(45deg, transparent 0 3px, ${stroke} 3px 4px), repeating-linear-gradient(-45deg, transparent 0 3px, ${stroke} 3px 4px)`,
    `radial-gradient(circle at 2px 2px, ${stroke} 0 1.2px, transparent 1.3px)`,
    `repeating-linear-gradient(90deg, transparent 0 1px, ${stroke} 1px 2px)`,
    `repeating-linear-gradient(0deg, transparent 0 1px, ${stroke} 1px 2px)`,
  ];

  return {
    backgroundColor: entry.color,
    backgroundImage: backgrounds[entry.patternIndex] ?? backgrounds[0],
    backgroundSize: '8px 8px',
  };
}

const hoveredCoverageBucket = ref<CoverageBucket | null>(null);

const coverageTooltipAoiNames = computed(() =>
  areas.value.filter((a) => a.visible).map((a) => a.name),
);

let resizeObserver: ResizeObserver | null = null;
let observedWidthEl: Element | null = null;
let observedHeightEl: Element | null = null;
let resizeObserverRafId: number | null = null;
let measureGraphRafId: number | null = null;

function clearMeasureGraphFrame() {
  if (measureGraphRafId != null && typeof window !== 'undefined') {
    window.cancelAnimationFrame(measureGraphRafId);
    measureGraphRafId = null;
  }
}

function updateGraphSize() {
  // Use graph-area for width (matches absolutely-positioned overlays),
  // and scroll container for height (determines available lane space).
  const widthEl = graphAreaContainer.value;
  const heightEl = graphScrollContainer.value || graphContainer.value;

  const width = widthEl?.getBoundingClientRect().width ?? 0;
  const height = heightEl?.getBoundingClientRect().height ?? 0;

  if (width > 0) graphWidth.value = Math.round(width);
  if (height > 0) graphHeight.value = Math.round(height);
}

function observeGraphElements() {
  if (!resizeObserver || typeof ResizeObserver === 'undefined') return;

  const widthEl = graphAreaContainer.value;
  const heightEl = graphScrollContainer.value || graphContainer.value;

  if (observedWidthEl && observedWidthEl !== widthEl) {
    resizeObserver.unobserve(observedWidthEl);
    observedWidthEl = null;
  }
  if (observedHeightEl && observedHeightEl !== heightEl) {
    resizeObserver.unobserve(observedHeightEl);
    observedHeightEl = null;
  }

  if (widthEl && observedWidthEl !== widthEl) {
    resizeObserver.observe(widthEl);
    observedWidthEl = widthEl;
  }
  if (heightEl && observedHeightEl !== heightEl) {
    resizeObserver.observe(heightEl);
    observedHeightEl = heightEl;
  }
}

function measureGraph() {
  if (!resizeObserver && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (resizeObserverRafId != null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(resizeObserverRafId);
      }
      resizeObserverRafId = window.requestAnimationFrame(() => {
        resizeObserverRafId = null;
        updateGraphSize();
        observeGraphElements();
      });
    });
  }

  observeGraphElements();
  updateGraphSize();
}

function scheduleMeasureGraph(extraFrames = 2) {
  nextTick(() => {
    measureGraph();
    if (typeof window === 'undefined' || extraFrames <= 0) return;

    clearMeasureGraphFrame();
    const step = (remainingFrames: number) => {
      measureGraphRafId = window.requestAnimationFrame(() => {
        measureGraph();
        if (remainingFrames > 1) {
          step(remainingFrames - 1);
        } else {
          measureGraphRafId = null;
        }
      });
    };
    step(extraFrames);
  });
}

function onWindowResize() {
  scheduleMeasureGraph(2);
}

onMounted(() => {
  scheduleMeasureGraph(2);
  window.addEventListener('resize', onWindowResize);
  document.addEventListener('click', onClickOutsideUtilityPanel);
  document.addEventListener('keydown', onUtilityPanelEscape);
  window.addEventListener('resize', onUtilityPanelViewportResize);
  window.visualViewport?.addEventListener('resize', onUtilityPanelViewportResize);
  syncUtilityPanelObservers();
  if (props.visible && activeUtilityPanel.value) {
    nextTick(() => {
      updateUtilityPanelLayout();
      requestAnimationFrame(() => {
        updateUtilityPanelLayout();
        utilityFloatingPanel.value?.focus();
      });
    });
  }
});
onBeforeUnmount(() => {
  clearMeasureGraphFrame();
  if (autoOpenMonitorTimer) clearTimeout(autoOpenMonitorTimer);
  if (resizeObserverRafId != null && typeof window !== 'undefined') {
    window.cancelAnimationFrame(resizeObserverRafId);
  }
  resizeObserver?.disconnect();
  window.removeEventListener('resize', onWindowResize);
});

watch(graphMode, (mode) => {
  scheduleMeasureGraph(2);
  try { localStorage.setItem(GRAPH_MODE_STORAGE_KEY, mode); } catch { /* ignore */ }
}, { flush: 'post' });
watch(() => props.aoiMonitorAutoOpenVersion, () => {
  hasAutoOpenedAoiMonitorPanel.value = false;
});
watch(mapViewMode, (mode, prevMode) => {
  if (mode !== 'aoi' && graphMode.value === 'coverage') graphMode.value = 'grouped';
  if (mode !== 'aoi' && activeUtilityPanel.value === 'monitor') activeUtilityPanel.value = null;
  if (mode !== 'aoi' || prevMode === 'aoi') return;
  // Opened once per session normally — but every time when no areas are saved.
  // With nothing to monitor the AOI view is blank and offers no next step, so a
  // user who closed the panel and came back would land on an empty view with no
  // prompt to upload anything. The panel doubles as the add-area dropzone, and
  // its launcher already relabels itself to "Add area" in that state.
  if (!hasAreas.value || !hasAutoOpenedAoiMonitorPanel.value) autoOpenMonitorPanel();
});
watch(activeTab, (tab) => {
  if (tab === 'graph' && props.visible) scheduleMeasureGraph(2);
});
watch([fullscreen, panelHeight], () => scheduleMeasureGraph(2), { flush: 'post' });
watch(() => props.visible, (isVisible) => {
  if (!isVisible) {
    activeUtilityPanel.value = null;
    return;
  }

  if (activeTab.value === 'graph') scheduleMeasureGraph(2);
  nextTick(() => {
    syncUtilityPanelObservers();
    updateUtilityPanelLayout();
  });
}, { flush: 'post' });
watch(
  [graphAreaContainer, graphScrollContainer, graphContainer],
  () => scheduleMeasureGraph(2),
  { flush: 'post' },
);
watch(
  [hasAreas, () => selectedHexCells.value.size, mapViewMode],
  () => scheduleMeasureGraph(2),
  { flush: 'post' },
);

let syncingScroll = false;
function onGraphScroll() {
  if (syncingScroll) return;
  syncingScroll = true;
  const sc = graphScrollContainer.value;
  const lc = swimLabelContainer.value;
  if (sc && lc) lc.scrollTop = sc.scrollTop;
  syncingScroll = false;
}
function onLabelScroll() {
  if (syncingScroll) return;
  syncingScroll = true;
  const sc = graphScrollContainer.value;
  const lc = swimLabelContainer.value;
  if (sc && lc) sc.scrollTop = lc.scrollTop;
  syncingScroll = false;
}

const dragAxisRange = ref<[number, number] | null>(null);
const axisMin = computed(() => dragAxisRange.value?.[0] ?? timeRange.value[0]);
const axisMax = computed(() => dragAxisRange.value?.[1] ?? timeRange.value[1]);
const axisSpan = computed(() => Math.max(1, axisMax.value - axisMin.value));

const handleLeftPx = computed(() => {
  const px = timeToPx(timeRange.value[0]);
  return Math.max(graphPadX, Math.min(graphPadX + usableWidth.value, px));
});
const handleRightPx = computed(() => {
  const px = timeToPx(timeRange.value[1]);
  return Math.max(graphPadX, Math.min(graphPadX + usableWidth.value, px));
});

const EDGE_BREATHING_PX = 6;
const EXTREME_EDGE_EPSILON_MS = 1000;
const leftHandleAtExtreme = computed(() =>
  Math.abs(timeRange.value[0] - axisMin.value) <= EXTREME_EDGE_EPSILON_MS,
);
const rightHandleAtExtreme = computed(() =>
  Math.abs(timeRange.value[1] - axisMax.value) <= EXTREME_EDGE_EPSILON_MS,
);

function timeToPx(t: number): number {
  return graphPadX + ((t - axisMin.value) / axisSpan.value) * usableWidth.value;
}
function pxToTime(px: number): number {
  return axisMin.value + ((px - graphPadX) / usableWidth.value) * axisSpan.value;
}

function clampGraphBarPx(px: number): number {
  const minPx = graphPadX + (leftHandleAtExtreme.value ? EDGE_BREATHING_PX : 0);
  const maxPx = graphPadX + usableWidth.value - (rightHandleAtExtreme.value ? EDGE_BREATHING_PX : 0);
  return Math.max(minPx, Math.min(maxPx, px));
}

interface AxisTick { time: number; x: number; type: '3h' | '6h' | '12h' | '24h'; label: string; showLabel: boolean; }

const axisTicks = computed<AxisTick[]>(() => {
  const ticks: AxisTick[] = [];
  if (!axisMin.value || !axisMax.value) return ticks;

  const ONE_HOUR = 60 * 60 * 1000;
  let t = Math.floor(axisMin.value / ONE_HOUR) * ONE_HOUR;
  if (t < axisMin.value) t += ONE_HOUR;

  while (t <= axisMax.value) {
    const h = getHourInEffectiveTimeZone(t);

    if (h % 3 !== 0) {
      t += ONE_HOUR;
      continue;
    }

    let type: '3h' | '6h' | '12h' | '24h';
    let showLabel = false;
    let label = '';

    if (h === 0) {
      type = '24h';
      showLabel = true;
      label = formatDateShort(t);
    } else if (h === 12) {
      type = '12h';
      showLabel = true;
      label = formatTimeShort(t);
    } else if (h % 6 === 0) {
      type = '6h';
      showLabel = true;
      label = formatTimeShort(t);
    } else {
      type = '3h';
    }

    ticks.push({ time: t, x: timeToPx(t), type, showLabel, label });
    t += ONE_HOUR;
  }

  return ticks;
});

function getPassHoursToPass(pass: GroupedVisiblePass): number {
  const startMs = parseUtcEpochMs(pass.start_time);
  if (!Number.isFinite(startMs)) return NO_UPCOMING_HOURS;
  return Math.max(0, (startMs - timeRange.value[0]) / 3_600_000);
}

function barColor(pass: GroupedVisiblePass, outsideRange = false, overrideAlpha?: number): string {
  const alpha = overrideAlpha ?? (outsideRange ? 0.3 : 0.82);
  const hours = getPassHoursToPass(pass);
  return getTimingColorForHours(hours, alpha, alpha);
}

interface SwimLane { name: string; shortName: string; rowH: number; yOffset: number; }

const swimLanes = computed<SwimLane[]>(() => {
  if (!graphPasses.value.length) return [];

  const names: string[] = [];
  for (const p of graphPasses.value) {
    if (!names.includes(p.name)) names.push(p.name);
  }

  const rowCount = names.length || 1;
  const availableH = graphHeight.value - axisAreaHeight;
  const rowH = Math.max(MIN_LANE_HEIGHT, availableH / rowCount);
  let yOffset = 0;

  return names.map((name) => {
    const lane: SwimLane = {
      name,
      shortName: name,
      rowH,
      yOffset,
    };
    yOffset += rowH;
    return lane;
  });
});

const sensorSwimLanes = computed<SwimLane[]>(() => {
  if (!graphPasses.value.length) return [];

  const sensorTypes: string[] = [];
  for (const p of graphPasses.value) {
    const st = p.sensor_type ?? 'Unknown';
    if (!sensorTypes.includes(st)) sensorTypes.push(st);
  }

  const rowCount = sensorTypes.length || 1;
  const availableH = graphHeight.value - axisAreaHeight;
  const rowH = Math.max(MIN_LANE_HEIGHT, availableH / rowCount);
  let yOffset = 0;

  return sensorTypes.map((sensorType) => {
    const lane: SwimLane = { name: sensorType, shortName: sensorType, rowH, yOffset };
    yOffset += rowH;
    return lane;
  });
});

const activeSwimLanes = computed<SwimLane[]>(() => {
  if (graphMode.value === 'sensor') return sensorSwimLanes.value;
  return swimLanes.value;
});

interface BarDef {
  pass: GroupedVisiblePass;
  x: number;
  y: number;
  w: number;
  h: number;
  outsideRange: boolean;
  laneKey: string;
  laneTop: number;
  laneBottom: number;
  laneHeight: number;
}

const overpassBars = computed<BarDef[]>(() => {
  if (!graphPasses.value.length) return [];

  if (graphMode.value === 'combined') {
    const th = contentTrackHeight.value;
    const barH = Math.round(th * 0.72);
    const y = (th - barH) / 2;

    return graphPasses.value.map((pass) => {
      const startMs = parseUtcEpochMs(pass.start_time);
      const endMs = parseUtcEpochMs(pass.end_time);
      const outsideRange = endMs < timeRange.value[0] || startMs > timeRange.value[1];
      const startPx = clampGraphBarPx(timeToPx(startMs));
      const endPx = clampGraphBarPx(timeToPx(endMs));
      const rawW = Math.abs(endPx - startPx);
      const w = Math.max(MIN_BAR_WIDTH, rawW);
      const midPx = (startPx + endPx) / 2;
      const x = midPx - w / 2;
      return {
        pass,
        x,
        y,
        w,
        h: barH,
        outsideRange,
        laneKey: 'combined',
        laneTop: 0,
        laneBottom: th,
        laneHeight: th,
      };
    });
  }

  if (!activeSwimLanes.value.length) return [];
  const laneMap = new Map<string, SwimLane>();
  for (const lane of activeSwimLanes.value) laneMap.set(lane.name, lane);
  const firstLaneH = activeSwimLanes.value[0]?.rowH ?? barHeight;
  // Scale bar height with lane: at MIN_LANE_HEIGHT use barHeight, grow up to 70% of lane
  const actualBarH = Math.round(Math.max(barHeight, Math.min(firstLaneH * 0.7, firstLaneH - 8)));
  const isSensor = graphMode.value === 'sensor';

  return graphPasses.value.map((pass) => {
    const startMs = parseUtcEpochMs(pass.start_time);
    const endMs = parseUtcEpochMs(pass.end_time);
    const outsideRange = endMs < timeRange.value[0] || startMs > timeRange.value[1];
    const startPx = clampGraphBarPx(timeToPx(startMs));
    const endPx = clampGraphBarPx(timeToPx(endMs));
    const rawW = Math.abs(endPx - startPx);
    const w = Math.max(MIN_BAR_WIDTH, rawW);
    const midPx = (startPx + endPx) / 2;
    const x = midPx - w / 2;
    const laneKey = isSensor ? (pass.sensor_type ?? 'Unknown') : pass.name;
    const lane = laneMap.get(laneKey);
    const laneY = lane ? lane.yOffset : 0;
    const laneRowH = lane ? lane.rowH : barHeight;
    const y = laneY + (laneRowH - actualBarH) / 2;
    return {
      pass,
      x,
      y,
      w,
      h: actualBarH,
      outsideRange,
      laneKey,
      laneTop: laneY,
      laneBottom: laneY + laneRowH,
      laneHeight: laneRowH,
    };
  });
});

/** Cloud cover overlay bars: renders a proportional semi-transparent white strip on bars with cloud data */
const cloudOverlayBars = computed(() => {
  return overpassBars.value
    .filter((bar) => bar.pass.cloud_cover_pct != null && bar.pass.cloud_cover_pct > 0 && !bar.outsideRange)
    .map((bar) => {
      const pct = bar.pass.cloud_cover_pct!;
      const cloudH = Math.max(1, (pct / 100) * bar.h);
      const alpha = 0.25 + (pct / 100) * 0.45; // 25%–70% opacity
      return {
        ...bar,
        cloudH,
        cloudFill: `rgba(180, 195, 210, ${alpha})`,
      };
    });
});

/** Delivery latency whiskers: thin dashed line from bar end to expected delivery time */
interface DeliveryWhisker {
  pass: GroupedVisiblePass;
  path: string;
  x2: number;
  tickY1: number;
  tickY2: number;
}

interface DeliveryWhiskerCandidate {
  pass: GroupedVisiblePass;
  x1: number;
  x2: number;
  anchorY: number;
  tickHalf: number;
  laneKey: string;
  laneTop: number;
  laneBottom: number;
  laneHeight: number;
}

const WHISKER_COLLISION_GAP_PX = 4;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function getWhiskerRailBounds(candidate: DeliveryWhiskerCandidate): { minY: number; maxY: number } {
  const tickInset = candidate.tickHalf + 2;
  const preferredInset = clamp(candidate.laneHeight * 0.18, 2, 8);

  let minY = candidate.laneTop + tickInset + preferredInset;
  let maxY = candidate.laneBottom - tickInset - preferredInset;

  if (maxY < minY) {
    minY = candidate.laneTop + tickInset;
    maxY = candidate.laneBottom - tickInset;
  }
  if (maxY < minY) {
    const midY = (candidate.laneTop + candidate.laneBottom) / 2;
    return { minY: midY, maxY: midY };
  }
  return { minY, maxY };
}

function buildWhiskerPath(candidate: DeliveryWhiskerCandidate, railY: number): string {
  return `M ${candidate.x1} ${railY} L ${candidate.x2} ${railY}`;
}

const deliveryWhiskers = computed<DeliveryWhisker[]>(() => {
  if (graphMode.value === 'coverage') return [];
  const whiskers: DeliveryWhisker[] = [];
  const candidatesByLane = new Map<string, DeliveryWhiskerCandidate[]>();

  for (const bar of overpassBars.value) {
    if (bar.outsideRange) continue;
    const deliveryIso = computeExpectedDeliveryForPass(bar.pass);
    if (!deliveryIso) continue;
    const deliveryMs = new Date(deliveryIso).getTime();
    if (!Number.isFinite(deliveryMs)) continue;
    const x2 = clampGraphBarPx(timeToPx(deliveryMs));
    const x1 = bar.x + bar.w;
    if (x2 - x1 < 2) continue; // too short to be visible
    const midY = bar.y + bar.h / 2;
    const tickHalf = Math.max(2, bar.h * 0.25);
    const candidate: DeliveryWhiskerCandidate = {
      pass: bar.pass,
      x1,
      x2,
      anchorY: midY,
      tickHalf,
      laneKey: bar.laneKey,
      laneTop: bar.laneTop,
      laneBottom: bar.laneBottom,
      laneHeight: bar.laneHeight,
    };
    const laneCandidates = candidatesByLane.get(candidate.laneKey);
    if (laneCandidates) laneCandidates.push(candidate);
    else candidatesByLane.set(candidate.laneKey, [candidate]);
  }

  for (const laneCandidates of candidatesByLane.values()) {
    laneCandidates.sort((a, b) => a.x1 - b.x1 || a.x2 - b.x2);

    const clusters: DeliveryWhiskerCandidate[][] = [];
    let cluster: DeliveryWhiskerCandidate[] = [];
    let clusterEnd = -Infinity;

    for (const candidate of laneCandidates) {
      if (!cluster.length || candidate.x1 <= clusterEnd + WHISKER_COLLISION_GAP_PX) {
        cluster.push(candidate);
        clusterEnd = Math.max(clusterEnd, candidate.x2);
        continue;
      }

      clusters.push(cluster);
      cluster = [candidate];
      clusterEnd = candidate.x2;
    }

    if (cluster.length) clusters.push(cluster);

    for (const clusterCandidates of clusters) {
      const levelEnds: number[] = [];
      const assigned = clusterCandidates.map((candidate) => {
        let level = levelEnds.findIndex((endX) => candidate.x1 > endX + WHISKER_COLLISION_GAP_PX);
        if (level === -1) {
          level = levelEnds.length;
          levelEnds.push(candidate.x2);
        } else {
          levelEnds[level] = candidate.x2;
        }
        return { candidate, level };
      });

      const levelCount = assigned.reduce((maxLevel, item) => Math.max(maxLevel, item.level), 0) + 1;

      for (const { candidate, level } of assigned) {
        const { minY, maxY } = getWhiskerRailBounds(candidate);
        const railY = levelCount <= 1 || Math.abs(maxY - minY) < 1
          ? clamp(candidate.anchorY, minY, maxY)
          : minY + (level / (levelCount - 1)) * (maxY - minY);

        whiskers.push({
          pass: candidate.pass,
          path: buildWhiskerPath(candidate, railY),
          x2: candidate.x2,
          tickY1: clamp(railY - candidate.tickHalf, candidate.laneTop, candidate.laneBottom),
          tickY2: clamp(railY + candidate.tickHalf, candidate.laneTop, candidate.laneBottom),
        });
      }
    }
  }

  return whiskers;
});

function getBarFill(bar: BarDef): string {
  if (bar.outsideRange) return barColor(bar.pass, true);
  const isSelected = isPassSelected(bar.pass);
  const isHovered = isPassHovered(bar.pass);
  if (isSelected) return getTimingColorForHours(getPassHoursToPass(bar.pass), 0.95, 0.95);
  if (isHovered) return getTimingColorForHours(getPassHoursToPass(bar.pass), 0.9, 0.9);
  // Combined mode: semi-transparent so overlapping bars visually blend
  if (graphMode.value === 'combined') return barColor(bar.pass, false, 0.88);
  return barColor(bar.pass, false);
}

function getBarStroke(bar: BarDef): string {
  if (bar.outsideRange) return 'var(--timeline-bar-stroke-outside)';
  if (isPassSelected(bar.pass)) return '#03DECC';
  if (isPassHovered(bar.pass)) return 'rgba(3, 222, 204, 0.75)';
  return 'var(--timeline-bar-stroke)';
}

function getBarStrokeWidth(bar: BarDef): number {
  if (bar.outsideRange) return 0.5;
  if (isPassSelected(bar.pass)) return 1.5;
  if (isPassHovered(bar.pass)) return 1.25;
  return 1.15;
}

function isPassSelected(pass: GroupedVisiblePass): boolean {
  return groupedPassContainsRawId(pass, selectedPassId.value);
}

function isPassHovered(pass: GroupedVisiblePass): boolean {
  return groupedPassContainsRawId(pass, hoveredPassId.value);
}

function isWhiskerHighlighted(pass: GroupedVisiblePass): boolean {
  return isPassSelected(pass) || isPassHovered(pass);
}

function getWhiskerStroke(pass: GroupedVisiblePass): string {
  if (isPassSelected(pass)) return '#7CFFF4';
  if (isPassHovered(pass)) return 'rgba(94, 255, 244, 0.96)';
  return svgColors.value.whisker;
}

function getWhiskerHighlightStroke(pass: GroupedVisiblePass): string {
  if (isPassSelected(pass)) return 'rgba(94, 255, 244, 0.42)';
  if (isPassHovered(pass)) return 'rgba(94, 255, 244, 0.3)';
  return 'transparent';
}

function getWhiskerTickStroke(pass: GroupedVisiblePass): string {
  if (isPassSelected(pass)) return '#B4FFF9';
  if (isPassHovered(pass)) return 'rgba(124, 255, 244, 1)';
  return svgColors.value.whiskerTick;
}

function getWhiskerStrokeWidth(pass: GroupedVisiblePass): number {
  if (isPassSelected(pass)) return 1.8;
  if (isPassHovered(pass)) return 1.4;
  return 1;
}

function getWhiskerTickStrokeWidth(pass: GroupedVisiblePass): number {
  if (isPassSelected(pass)) return 2.5;
  if (isPassHovered(pass)) return 2.1;
  return 1.5;
}

function getWhiskerHighlightStrokeWidth(pass: GroupedVisiblePass): number {
  if (isPassSelected(pass)) return 5;
  if (isPassHovered(pass)) return 4;
  return 0;
}

function getWhiskerHighlightTickStrokeWidth(pass: GroupedVisiblePass): number {
  if (isPassSelected(pass)) return 5.5;
  if (isPassHovered(pass)) return 4.5;
  return 0;
}

function getWhiskerDasharray(pass: GroupedVisiblePass): string {
  if (isPassSelected(pass)) return '4,2';
  if (isPassHovered(pass)) return '3.5,2';
  return '3,2';
}

const hoveredBarRawId = ref<string | null>(null);
const hoveredBar = ref<BarDef | null>(null);
const tooltipScreenX = ref(0);
const tooltipScreenY = ref(0);

function getHoveredLatency(): number | null {
  const bar = hoveredBar.value;
  if (!bar) return null;
  return getExpectedLatencyMinutesForPass(bar.pass);
}

function getHoveredDelivery(): string | null {
  const bar = hoveredBar.value;
  if (!bar) return null;
  return computeExpectedDeliveryForPass(bar.pass);
}

function mouseToSvg(e: MouseEvent): { sx: number; sy: number } | null {
  const rect = graphContainer.value?.getBoundingClientRect();
  if (!rect) return null;
  const mx = e.clientX - rect.left;
  const my = e.clientY - rect.top;
  const sx = (mx / rect.width) * graphWidth.value;
  const sy = (my / rect.height) * svgTotalHeight.value;
  return { sx, sy };
}

function onGraphMouseMove(e: MouseEvent) {
  const pt = mouseToSvg(e);
  if (!pt) return;

  tooltipScreenX.value = e.clientX;
  tooltipScreenY.value = e.clientY - 12;

  if (graphMode.value === 'coverage') {
    const drawH = coverageChartH.value - COVERAGE_PAD_TOP - COVERAGE_PAD_BOTTOM;
    const hit = coverageBuckets.value.find((b) => {
      const x1 = Math.min(timeToPx(b.startMs), timeToPx(b.endMs));
      const x2 = Math.max(timeToPx(b.startMs), timeToPx(b.endMs));
      return pt.sx >= x1 && pt.sx <= x2 && pt.sy >= COVERAGE_PAD_TOP && pt.sy <= COVERAGE_PAD_TOP + drawH;
    });
    hoveredCoverageBucket.value = hit ?? null;
    hoveredBar.value = null;
    hoveredBarRawId.value = null;

    // Highlight matching coverage features on the map with coverage teal color
    if (hit) {
      const ids = hit.passIds;
      setHoveredPass(ids[0] ?? null, ids, true);
    } else {
      setHoveredPass(null);
    }
    return;
  }

  hoveredCoverageBucket.value = null;
  const bar = overpassBars.value.find(
    (b) => pt.sx >= b.x - HIT_PAD_X && pt.sx <= b.x + b.w + HIT_PAD_X && pt.sy >= b.y - HIT_PAD_Y && pt.sy <= b.y + b.h + HIT_PAD_Y,
  );
  hoveredBarRawId.value = bar ? getPrimaryPassId(bar.pass) : null;
  hoveredBar.value = bar ?? null;
  setHoveredPass(hoveredBarRawId.value, bar?.pass.sourcePassIds);
}

function onGraphMouseLeave() {
  if (hoveredPassId.value === hoveredBarRawId.value || hoveredCoverageBucket.value) {
    setHoveredPass(null);
  }
  hoveredBarRawId.value = null;
  hoveredBar.value = null;
  hoveredCoverageBucket.value = null;
}

function onGraphClick(e: MouseEvent) {
  if (graphMode.value === 'coverage') return;
  const pt = mouseToSvg(e);
  if (!pt) return;
  const bar = overpassBars.value.find(
    (b) => pt.sx >= b.x - HIT_PAD_X && pt.sx <= b.x + b.w + HIT_PAD_X && pt.sy >= b.y - HIT_PAD_Y && pt.sy <= b.y + b.h + HIT_PAD_Y,
  );
  if (bar) selectPass(bar.pass);
}

let dragging: 'left' | 'right' | null = null;

function startDrag(handle: 'left' | 'right') {
  dragAxisRange.value = [...timeRange.value];
  dragging = handle;
  window.addEventListener('mousemove', onDrag);
  window.addEventListener('mouseup', stopDrag);
}

function onDrag(e: MouseEvent) {
  if (!dragging || !graphContainer.value) return;
  const rect = graphContainer.value.getBoundingClientRect();
  const px = Math.max(0, Math.min(graphWidth.value, (e.clientX - rect.left) * (graphWidth.value / rect.width)));
  let t = pxToTime(px);
  t = Math.max(axisMin.value, Math.min(axisMax.value, t));
  if (dragging === 'left') {
    timeRange.value = [Math.min(t, timeRange.value[1] - 60000), timeRange.value[1]];
  } else {
    timeRange.value = [timeRange.value[0], Math.max(t, timeRange.value[0] + 60000)];
  }
}

function stopDrag() {
  dragging = null;
  dragAxisRange.value = null;
  window.removeEventListener('mousemove', onDrag);
  window.removeEventListener('mouseup', stopDrag);
}
</script>
